/**
 * Mention Parsing Utilities
 * Handles parsing @ mentions from message text and extracting user information
 */

import { Mention } from '@/types/messaging';

export interface UserOption {
  id: string;
  name: string;
  email: string;
  picture?: string;
}

/**
 * Parse mentions from message text
 * Finds @username patterns and matches them to users
 * 
 * @param text - The message text containing @ mentions
 * @param availableUsers - List of users that can be mentioned (conversation participants or org users)
 * @returns Array of Mention objects with user info and text positions
 */
export const parseMentions = (
  text: string,
  availableUsers: UserOption[]
): Mention[] => {
  const mentions: Mention[] = [];
  
  // Sort users by name length (longest first) to match full names before partial names
  const sortedUsers = [...availableUsers].sort((a, b) => {
    const aName = (a.name || '').length;
    const bName = (b.name || '').length;
    return bName - aName;
  });
  
  // Find all @ symbols in the text
  let searchStart = 0;
  while (true) {
    const atIndex = text.indexOf('@', searchStart);
    if (atIndex === -1) break;
    
    const textAfterAt = text.substring(atIndex + 1);
    let bestMatch: { user: UserOption; matchedText: string; endIndex: number } | null = null;
    
    // Try to match against all users (sorted by length, so longest matches first)
    for (const user of sortedUsers) {
      const userName = (user.name || user.email || '').trim();
      if (!userName) continue;
      
      const lowerUserName = userName.toLowerCase();
      const lowerTextAfterAt = textAfterAt.toLowerCase();
      
      // Check if text after @ starts with the user name
      if (lowerTextAfterAt.startsWith(lowerUserName)) {
        const charAfterName = textAfterAt[userName.length];
        // Check if it's followed by space, punctuation, or end of string
        // This ensures we don't match "Dhaval Chokshi" when text is "Dhaval Chokshiweb"
        if (charAfterName === undefined || /[\s,.;:!?\-]/.test(charAfterName)) {
          bestMatch = {
            user,
            matchedText: userName,
            endIndex: atIndex + 1 + userName.length // +1 for @
          };
          break; // Use longest match (since sorted by length)
        }
      }
    }
    
    if (bestMatch) {
      mentions.push({
        userId: bestMatch.user.id,
        userName: bestMatch.user.name || bestMatch.user.email || 'Unknown',
        startIndex: atIndex,
        endIndex: bestMatch.endIndex
      });
      
      // Move search past this mention to find next @
      searchStart = bestMatch.endIndex;
    } else {
      // No match found, move to next @
      searchStart = atIndex + 1;
    }
  }
  
  // Return all mentions (don't remove duplicates - we want to highlight all occurrences)
  return mentions;
};

/**
 * Extract mention text from message text
 * Used to get the text portion that should trigger autocomplete
 * 
 * @param text - Full message text
 * @param cursorPosition - Current cursor position in text
 * @returns The mention query text (text after @) or null if not in a mention
 */
export const extractMentionQuery = (
  text: string,
  cursorPosition: number
): { query: string; startIndex: number } | null => {
  // Find the last @ before cursor position
  const textBeforeCursor = text.substring(0, cursorPosition);
  const lastAtIndex = textBeforeCursor.lastIndexOf('@');
  
  if (lastAtIndex === -1) {
    return null;
  }
  
  // Check if there's a space between @ and cursor (mention is complete)
  const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
  if (textAfterAt.includes(' ')) {
    return null;
  }
  
  // Extract query (text after @)
  const query = textAfterAt.trim();
  
  return {
    query,
    startIndex: lastAtIndex
  };
};

/**
 * Replace mention in text with formatted mention
 * Used when user selects a mention from autocomplete
 * 
 * @param text - Original text
 * @param mentionStartIndex - Position where @ starts
 * @param mentionEndIndex - Position where mention ends
 * @param userName - Name to insert
 * @returns New text with mention replaced
 */
export const insertMention = (
  text: string,
  mentionStartIndex: number,
  mentionEndIndex: number,
  userName: string
): string => {
  const before = text.substring(0, mentionStartIndex);
  const after = text.substring(mentionEndIndex);
  return `${before}@${userName} ${after}`;
};

/**
 * Check if text contains any valid mentions
 * 
 * @param text - Message text
 * @param availableUsers - List of available users
 * @returns true if text contains at least one valid mention
 */
export const hasMentions = (
  text: string,
  availableUsers: UserOption[]
): boolean => {
  const mentions = parseMentions(text, availableUsers);
  return mentions.length > 0;
};

